import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';

import { logEvent, type DomainEventType } from '../../../utils/eventLog';
import { formatUpdatedAgo } from '../../../utils/updatedAgo';
import { AM, AM_TEXT } from '../assignmentMonitoringTokens';
import {
  firstDay,
  formatLong,
  formatMonthDay,
  formatWeekday,
  lastDay,
  monthName,
  nextMonth,
  plural,
} from '../assignmentMonitoring';
import {
  fetchAssignmentMonitoring,
  saveAssignmentMonitoringSettings,
  type AssignmentMonitoringOverview,
  type Requirement,
  type SettingsChange,
} from '../assignmentMonitoringApi';
import { OutlineButton } from '../../AssignmentMonitoring/recordParts';
import { AssignmentMonitoringDrawer } from './AssignmentMonitoringDrawer';
import { LinkButton, NoteRow, OffPill, Pill, Skeleton, Toast } from './AssignmentMonitoringUi';

export interface AssignmentMonitoringCardProps {
  source?: () => Promise<AssignmentMonitoringOverview | null>;
  save?: (change: SettingsChange) => Promise<AssignmentMonitoringOverview>;
  now?: () => Date;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unavailable' }
  | { kind: 'ready'; overview: AssignmentMonitoringOverview };

export const ASSIGNMENT_RECORDS_ROUTE = '/dashboard/assignments';

export const ASSIGNMENT_COMING_SOON = 'A new assignment completion monitoring experience is coming soon.';

export function assignmentRecordRoute(month: string): string {
  return `${ASSIGNMENT_RECORDS_ROUTE}/${month}`;
}

const DEFAULT_REQUIREMENT: Requirement = { assign: 2, score: 1, excludeBenchmarkMonths: false };

function placeholderOverview(): AssignmentMonitoringOverview {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return {
    today: `${month}-${String(now.getDate()).padStart(2, '0')}`,
    month,
    on: false,
    everOn: false,
    requirement: DEFAULT_REQUIREMENT,
    drawerRequirement: DEFAULT_REQUIREMENT,
    isDefault: true,
    pending: null,
    months: [],
    skippedMonths: [],
    counts: null,
  };
}

function betaEventFor(change: SettingsChange): DomainEventType | null {
  switch (change.action) {
    case 'save':
      return 'progress_monitoring.requirement_changed.v1';
    case 'turnOff':
      return 'progress_monitoring.turned_off.v1';
    default:
      return null;
  }
}

function openRecord() {
  void logEvent('progress_monitoring.widget_opened.v1').catch(() => {});
}

function toastFor(change: SettingsChange, before: AssignmentMonitoringOverview): string {
  const nextFirst = firstDay(nextMonth(before.month));
  switch (change.action) {
    case 'save':
      return `Requirement saved. It starts ${formatLong(nextFirst)}.`;
    case 'turnOn':
      return `Assignment monitoring starts ${formatLong(nextFirst)}.`;
    case 'turnOff':
      return `Assignment monitoring stops after ${formatLong(lastDay(before.month))}.`;
    case 'cancelTurnOn':
      return 'Assignment monitoring stays off.';
    case 'keepOn':
      return 'Assignment monitoring stays on.';
  }
}

function Title({ off, caption }: { off?: boolean; caption?: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <Typography component="h2" sx={{ ...AM_TEXT.body1Bold, color: off ? AM.disabled : AM.ink }}>
          Assignment Monitoring
        </Typography>
        {off ? <OffPill /> : null}
      </Box>
      {caption ? (
        <Typography component="p" data-testid="am-refreshed" sx={{ ...AM_TEXT.body2, color: off ? AM.disabled : AM.muted, m: 0 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function BigRow({ big, label, sub, off }: { big: ReactNode; label: string; sub?: string; off?: boolean }) {
  const dash = big === '—';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <Typography
        component="span"
        sx={{ fontSize: '40px', lineHeight: '48px', fontWeight: 800, letterSpacing: '-1px', flexShrink: 0, color: dash || off ? AM.disabled : AM.ink }}
      >
        {big}
      </Typography>
      <Box>
        <Typography sx={{ ...AM_TEXT.body1Semi, color: off ? AM.disabled : AM.ink }}>{label}</Typography>
        {sub ? <Typography sx={{ ...AM_TEXT.body1, color: off ? AM.disabled : AM.muted }}>{sub}</Typography> : null}
      </Box>
    </Box>
  );
}

function TileLabels({ req, off }: { req: Requirement; off?: boolean }) {
  const sx = { flex: '1 1 0', color: off ? AM.disabled : AM.muted, ...AM_TEXT.overline };
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <Typography sx={sx}>{plural(req.assign, 'assignment')} per student</Typography>
      <Typography sx={sx}>{req.score} scored per student</Typography>
    </Box>
  );
}

function Tiles({ values, students, off }: { values: [ReactNode, ReactNode]; students?: number; off?: boolean }) {
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      {values.map((value, i) => (
        <Box
          key={i}
          data-testid="am-tile"
          sx={{
            flex: '1 1 0',
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
            padding: '12px 16px',
            borderRadius: '8px',
            bgcolor: AM.background,
            outline: `1px solid ${AM.divider}`,
            fontSize: '28px',
            lineHeight: '36px',
            fontWeight: 800,
            color: value === '—' || off ? AM.disabled : AM.ink,
          }}
        >
          {value}
          <Typography component="small" sx={{ ...AM_TEXT.body2, color: off ? AM.disabled : AM.muted }}>
            /{students === undefined ? 'students' : plural(students, 'student')}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Footer({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: left ? 'space-between' : 'flex-end', gap: '16px', borderTop: `1px solid ${AM.divider}`, pt: '12px' }}>
      {left}
      {right}
    </Box>
  );
}

function Shell({ off, children, footer }: { off?: boolean; children: ReactNode; footer: ReactNode }) {
  return (
    <Box
      data-testid="assignment-monitoring-card"
      sx={{
        bgcolor: off ? AM.grey25 : AM.surface,
        border: `1px solid ${AM.divider}`,
        borderRadius: '12px',
        p: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '20px',
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>{children}</Box>
      {footer}
    </Box>
  );
}

// The dashboard card. Status always lives in the note rows above the footer:
// a pending change, the previous month closing, a skipped month, a pending
// turn-off. The two rule boxes never carry a status colour.
export function AssignmentMonitoringCard({
  source = fetchAssignmentMonitoring,
  save = saveAssignmentMonitoringSettings,
  now = () => new Date(),
}: AssignmentMonitoringCardProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    let active = true;
    source()
      .then((overview) => {
        if (!active) return;
        // A caller with no district gets the coming-soon shell: the card is
        // always on the dashboard.
        setLoad(overview ? { kind: 'ready', overview } : { kind: 'unavailable' });
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.warn('assignment monitoring unavailable', err);
        setLoad({ kind: 'error' });
      });
    return () => {
      active = false;
    };
  }, [source]);

  const overview = load.kind === 'ready' ? load.overview : placeholderOverview();
  const onChange = async (change: SettingsChange): Promise<string | null> => {
    const updated = await save(change);
    const event = betaEventFor(change);
    if (event) void logEvent(event).catch(() => {});
    setLoad({ kind: 'ready', overview: updated });
    setDrawerOpen(false);
    const message = toastFor(change, overview);
    setToast(message);
    return message;
  };

  // The drawer edits the real rule only: nothing opens while the overview is
  // still loading or failed to load, so a placeholder can never be saved.
  const editButton = (label: string) => (
    <LinkButton
      label={label}
      muted
      disabled={load.kind !== 'ready'}
      startIcon={<EditOutlinedIcon sx={{ fontSize: 24 }} />}
      onClick={() => setDrawerOpen(true)}
    />
  );
  const seeDetails = (month: string) => (
    <LinkButton label="See details" to={assignmentRecordRoute(month)} onClick={openRecord} endIcon={<ChevronRightIcon sx={{ fontSize: 24 }} />} />
  );
  const overlays = (
    <>
      <AssignmentMonitoringDrawer open={drawerOpen && load.kind === 'ready'} overview={overview} onClose={() => setDrawerOpen(false)} onChange={onChange} />
      <Toast message={toast} onDone={clearToast} />
    </>
  );

  if (load.kind === 'loading') {
    return (
      <>
        <Shell footer={<Footer left={editButton('Edit requirements')} right={seeDetails(overview.month)} />}>
          <Title />
          <Typography sx={{ ...AM_TEXT.body2, color: AM.muted }}>Loading this month’s counts…</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Skeleton width={56} height={40} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Skeleton width={240} height={16} />
              <Skeleton width={140} height={16} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Skeleton height={80} flex={1} />
            <Skeleton height={80} flex={1} />
          </Box>
        </Shell>
        {overlays}
      </>
    );
  }

  const { counts, months } = overview;
  const current = months.find((m) => m.month === overview.month);
  const refreshed = formatUpdatedAgo(overview.refreshedAt, now());
  const errorCard = (
    <>
      <Shell footer={<Footer left={editButton('Edit requirements')} right={seeDetails(overview.month)} />}>
        <Title caption={refreshed} />
        <Typography sx={{ ...AM_TEXT.body1, color: AM.muted }}>
          This month’s counts couldn’t load right now. Try again in a moment.
        </Typography>
      </Shell>
      {overlays}
    </>
  );
  // TEMPORARY: until the assignment monitoring resolver is deployed the
  // overview request fails, so a failed or district-less read shows the
  // coming-soon shell from the Paper mockup instead of an error. Once the
  // resolver answers, the real card appears with no further change here.
  if (load.kind === 'error' || load.kind === 'unavailable') {
    return (
      <>
        <Shell
          off
          footer={
            <Footer
              left={<LinkButton label="Set up" muted disabled />}
              right={<LinkButton label="See details" muted disabled endIcon={<ChevronRightIcon sx={{ fontSize: 24 }} />} />}
            />
          }
        >
          <Title off caption={refreshed} />
          <Typography sx={{ ...AM_TEXT.body1Semi, color: AM.disabled }}>{ASSIGNMENT_COMING_SOON}</Typography>
          <TileLabels off req={DEFAULT_REQUIREMENT} />
          <Tiles off values={['—', '—']} />
        </Shell>
        {overlays}
      </>
    );
  }
  const previous = counts?.previous ?? null;
  const previousEntry = previous ? months.find((m) => m.month === previous.month) : undefined;
  const teachersMetLine = (n: number) => `/${n} ${n === 1 ? 'teacher' : 'teachers'} met the monthly requirement`;
  const previousNote =
    previous && previousEntry && previousEntry.phase === 'closing' && !previousEntry.skipped ? (
      <NoteRow key="previous" to={assignmentRecordRoute(previous.month)} onClick={openRecord} right={`${previous.teachersMet}/${previous.teachers} met · still counting`}>
        {monthName(previous.month)} · closes {formatWeekday(previous.closeDate)}
      </NoteRow>
    ) : null;
  const pendingNote = overview.pending ? (
    <NoteRow key="pending">
      Starting {formatMonthDay(firstDay(overview.pending.from))}: assign {overview.pending.assign} and score {overview.pending.score} per student
    </NoteRow>
  ) : null;
  const stopsNote = overview.stopsAfter ? (
    <NoteRow key="stops">Stops after {formatMonthDay(lastDay(overview.stopsAfter))} · past records stay available</NoteRow>
  ) : null;
  const defaultChip = overview.isDefault ? (
    <Box>
      <Pill label="Default" tone="indigo" />
    </Box>
  ) : null;

  if (!overview.on) {
    const scheduled = Boolean(overview.turnsOn);
    const sub = scheduled
      ? `Turns on ${formatWeekday(firstDay(overview.turnsOn!))}`
      : overview.offSince
        ? `Off since ${formatWeekday(firstDay(overview.offSince))}`
        : undefined;
    const last = months[months.length - 1];
    return (
      <>
        <Shell
          off
          footer={
            <Footer
              left={scheduled ? editButton('Edit requirements') : <OutlineButton label="Set up" onClick={() => setDrawerOpen(true)} />}
              right={last ? seeDetails(last.month) : undefined}
            />
          }
        >
          <Title off caption={refreshed} />
          <BigRow off big="—" label={teachersMetLine(counts?.teachers ?? 0)} sub={sub} />
          <TileLabels off req={overview.drawerRequirement} />
          <Tiles off values={['—', '—']} students={counts?.students ?? 0} />
        </Shell>
        {overlays}
      </>
    );
  }

  if (!counts) return errorCard;

  if (current?.skipped && current.window) {
    return (
      <>
        <Shell footer={<Footer left={editButton('Edit requirements')} right={seeDetails(overview.month)} />}>
          <Title caption={refreshed} />
          <BigRow big="—" label={teachersMetLine(counts.teachers)} sub={`${monthName(overview.month)} skipped`} />
          <TileLabels req={overview.requirement} />
          <Tiles values={['—', '—']} students={counts.students} />
          {defaultChip}
          {pendingNote}
          {previousNote}
          <NoteRow key="skipped">
            The {current.window.kind} window covers every day of {monthName(overview.month)}
          </NoteRow>
          {stopsNote}
        </Shell>
        {overlays}
      </>
    );
  }

  if (counts.teachers === 0) {
    return (
      <>
        <Shell footer={<Footer left={editButton('Edit requirements')} />}>
          <Title caption={refreshed} />
          <BigRow big="—" label={teachersMetLine(0)} sub="No teachers have rostered students yet" />
          <TileLabels req={overview.requirement} />
          <Tiles values={['—', '—']} students={0} />
          {defaultChip}
          {pendingNote}
          {previousNote}
          {stopsNote}
        </Shell>
        {overlays}
      </>
    );
  }

  return (
    <>
      <Shell footer={<Footer left={editButton('Edit requirements')} right={seeDetails(overview.month)} />}>
        <Title caption={refreshed} />
        <BigRow big={String(counts.teachersMet)} label={teachersMetLine(counts.teachers)} sub={`Closes ${formatWeekday(lastDay(overview.month))}`} />
        <TileLabels req={overview.requirement} />
        <Tiles values={[String(counts.assignedMet), String(counts.scoredMet)]} students={counts.students} />
        {defaultChip}
        {pendingNote}
        {previousNote}
        {stopsNote}
      </Shell>
      {overlays}
    </>
  );
}
